import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "../lib/format";

const CONFIRM_PHRASE = "HAPUS PERMANEN";

/**
 * ForceDeleteModal — Owner-only permanent cascade delete.
 *
 * Props:
 *   open, onOpenChange
 *   entityType: 'booking' | 'client' | 'employee'
 *   entityId, entityLabel  (e.g. "DWG-2025-0001 — Rani & Bagus")
 *   onDone()               (callback after successful delete)
 */
export default function ForceDeleteModal({ open, onOpenChange, entityType, entityId, entityLabel, onDone }) {
  const [step, setStep] = useState(1);            // 1 = preview, 2 = type confirm
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [typed, setTyped] = useState("");

  const previewPath = `/${entityType === "booking" ? "bookings" : entityType === "client" ? "clients" : "employees"}/${entityId}/force-delete-preview`;
  const deletePath  = `/${entityType === "booking" ? "bookings" : entityType === "client" ? "clients" : "employees"}/${entityId}/force-delete`;

  useEffect(() => {
    if (!open || !entityId) return;
    setStep(1); setTyped(""); setPreview(null); setLoading(true);
    api.get(previewPath)
      .then(({ data }) => setPreview(data))
      .catch((e) => {
        toast.error(formatApiError(e.response?.data?.detail) || "Gagal memuat preview");
        onOpenChange(false);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [open, entityId, entityType]);

  const submit = async () => {
    if (typed.trim() !== CONFIRM_PHRASE) {
      toast.error(`Ketik "${CONFIRM_PHRASE}" persis untuk melanjutkan`);
      return;
    }
    setBusy(true);
    try {
      await api.post(deletePath, {}, { headers: { "X-Confirm-Force-Delete": CONFIRM_PHRASE } });
      toast.success("Data dihapus permanen (Force Delete)");
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Force delete gagal");
    } finally {
      setBusy(false);
    }
  };

  const counts = preview?.counts || {};
  const rowEntries = Object.entries(counts).filter(([k, v]) => k !== "total_related" && v > 0);
  const totalRelated = counts.total_related || 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="max-w-lg" data-testid={`force-delete-modal-${entityType}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700">
            <AlertTriangle className="w-5 h-5"/> PERINGATAN — FORCE DELETE
          </DialogTitle>
          <DialogDescription className="text-rose-800 leading-relaxed">
            Tindakan ini akan menghapus <b>permanen</b> record beserta seluruh task, progress, dokumen, pembayaran, tagihan vendor, fee/upah dan data terkait. <b>Data tidak dapat dipulihkan.</b>
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center gap-2 py-8 text-[#5C6066] justify-center">
            <Loader2 className="w-5 h-5 animate-spin"/> Memuat ringkasan…
          </div>
        )}

        {!loading && preview && (
          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-sm">
              <div className="text-[10px] uppercase tracking-widest text-rose-700 font-semibold">Target</div>
              <div className="font-medium mt-0.5" data-testid="force-delete-target">{entityLabel}</div>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#8C9096] font-semibold mb-2">Rincian data yang akan dihapus</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-64 overflow-y-auto">
                {rowEntries.length === 0 && (
                  <div className="col-span-full text-xs text-[#8C9096]">Tidak ada record terkait.</div>
                )}
                {rowEntries.map(([k, v]) => (
                  <div key={k} className="p-2 rounded border border-rose-200 bg-white" data-testid={`force-delete-count-${k}`}>
                    <div className="font-mono text-lg font-semibold text-rose-800">{v}</div>
                    <div className="text-[10px] uppercase tracking-wide text-[#5C6066]">{k.replace(/_/g, " ")}</div>
                  </div>
                ))}
              </div>
              <div className="mt-3 text-xs text-[#5C6066]">
                Total record terkait: <b className="text-rose-800">{totalRelated}</b>
              </div>
            </div>

            {step === 2 && (
              <div className="pt-2 border-t border-rose-200">
                <Label className="text-xs uppercase tracking-widest text-rose-700 font-semibold">
                  Ketik "<span className="font-mono">{CONFIRM_PHRASE}</span>" untuk konfirmasi
                </Label>
                <Input
                  autoFocus
                  className="mt-1.5 font-mono"
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  placeholder={CONFIRM_PHRASE}
                  data-testid="force-delete-confirm-input"
                />
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Batal</Button>
          {step === 1 && !loading && (
            <Button
              onClick={() => setStep(2)}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="force-delete-next"
            >
              <Trash2 className="w-4 h-4 mr-1.5"/> Lanjut ke Konfirmasi
            </Button>
          )}
          {step === 2 && (
            <Button
              onClick={submit}
              disabled={busy || typed.trim() !== CONFIRM_PHRASE}
              className="bg-rose-700 hover:bg-rose-800 text-white"
              data-testid="force-delete-confirm-btn"
            >
              {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin"/> Menghapus…</> : <><Trash2 className="w-4 h-4 mr-1.5"/> Hapus Permanen</>}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
